// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// VIEW 1 — The average lies.
//
// The question: what share of a year's particle exposure arrived in a handful of hours, and
// does the annual mean the governments publish describe any real day?
//
// The form is a Lorenz curve, one small multiple per station-year, because that is the
// canonical way to draw concentration-of-share. A bar chart of annual means cannot say this at
// all — it is the shape being argued against.

import type { Bundle } from '../data';
import type { StationYear } from '../types';
import { el, legend, panel, stat, statRow, svg } from '../components/ui';
import { concentration, number, percent, prettyHour, tip, UNIT } from '../format';
import { ACCENT, JURISDICTION_COLOUR } from '../palette';
import { glossaryLink } from '../glossary';
import { getState, setState } from '../state';
import { openStationYear } from './stationDetail';

const CELL = 148;

function lorenzCell(row: StationYear, selected: boolean): HTMLElement {
  const node = el('button', `lorenz-cell${selected ? ' cell-on' : ''}`);
  node.type = 'button';
  node.setAttribute(
    'data-tip',
    tip(
      `${row.name} (${row.jurisdiction}), ${row.year}`,
      `Worst 1% of hours (${row.top1Hours}) carry ${percent(row.top1Share)} of the year's exposure`,
      `Annual mean ${concentration(row.annualMean)} ${UNIT} · 95th percentile hour ${concentration(row.p95)}`,
      `Worst single hour ${concentration(row.worstHourValue)} ${UNIT} at ${prettyHour(row.worstHourAt)}`,
      `${number(row.validHours)} valid hours — ${percent(row.capture, 0)} data capture`
    )
  );

  const readout = el('div', 'lorenz-readout');
  readout.textContent = percent(row.top1Share, 0);
  readout.style.color = JURISDICTION_COLOUR[row.jurisdiction] ?? 'var(--text-primary)';
  node.append(readout);
  node.append(el('div', 'lorenz-caption', 'of the year, in its worst 88 hours'));

  // The curve is a square by definition — cumulative share against cumulative share — and must
  // never be squashed to fit a card.
  const chart = svg('svg', {
    viewBox: `0 0 ${CELL} ${CELL}`,
    class: 'chart',
    role: 'img',
    'aria-label': `Lorenz curve for ${row.name} in ${row.year}`,
    preserveAspectRatio: 'xMidYMid meet',
  });
  chart.style.width = '100%';
  chart.style.height = 'auto';
  chart.style.marginTop = '6px';

  chart.append(
    svg('rect', { x: 0, y: 0, width: CELL, height: CELL, fill: 'var(--bg-surface)', rx: 4 })
  );
  // The diagonal: what the year would look like if every hour were the annual mean.
  chart.append(
    svg('line', {
      x1: 0, y1: CELL, x2: CELL, y2: 0,
      stroke: 'var(--border-strong)', 'stroke-width': 1, 'stroke-dasharray': '3 3',
    })
  );

  const points = row.curve.map(([x, y]) => `${(x * CELL).toFixed(1)},${(CELL - y * CELL).toFixed(1)}`);
  if (points.length > 1) {
    chart.append(
      svg('polyline', {
        points: points.join(' '),
        fill: 'none',
        stroke: JURISDICTION_COLOUR[row.jurisdiction] ?? 'var(--accent-primary)',
        'stroke-width': 2,
        'stroke-linejoin': 'round',
      })
    );
  }
  // The 1% mark, which is the number in the readout.
  const markX = 0.01 * CELL;
  chart.append(
    svg('line', {
      x1: markX, y1: 0, x2: markX, y2: CELL,
      stroke: ACCENT, 'stroke-width': 1, opacity: 0.7,
    })
  );
  chart.append(
    svg('line', {
      x1: 0, y1: CELL - row.top1Share * CELL, x2: markX, y2: CELL - row.top1Share * CELL,
      stroke: ACCENT, 'stroke-width': 1.5,
    })
  );
  node.append(chart);

  const name = el('div', 'lorenz-name');
  name.textContent = `${row.name} · ${row.year}`;
  node.append(name);
  const meta = el('div', 'lorenz-caption');
  meta.textContent = `mean ${concentration(row.annualMean)} · worst hour ${concentration(row.worstHourValue)}`;
  node.append(meta);

  node.addEventListener('click', () => openStationYear(row));
  return node;
}

export function renderAverage({ bundle, container }: { bundle: Bundle; container: HTMLElement }): void {
  const { meta, lorenz } = bundle;
  const state = getState();

  const headline = panel({
    title: 'The average lies',
    subtitle:
      'Australian air is clean on average and dangerous in bursts. This is how big the bursts are, measured against everything else that happened that year.',
  });

  const share = meta.headline.top1Share;
  headline.body.append(
    statRow(
      stat(
        percent(share),
        'of a year’s PM2.5 exposure',
        `arrives in its worst 1% of hours — about 88 of them`,
        tip(
          `Median across ${number(meta.headline.stationYears)} station-years`,
          `Interquartile range ${percent(meta.headline.iqr[0])} to ${percent(meta.headline.iqr[1])}`,
          'Computed on valid hours only, never on the 8,760 calendar hours'
        )
      ),
      stat(
        number(meta.headline.stationYears),
        'station-years',
        'each with at least 75% data capture',
        tip('A station-year below 75% capture is excluded rather than estimated')
      ),
      stat(
        percent(meta.headline.strictCapture),
        'at 95% capture',
        'the same figure on near-complete years only',
        tip(
          'If the headline depended on the capture threshold it would not be a finding.',
          `${number(meta.headline.strictCaptureStationYears)} station-years qualify at 95%`
        )
      ),
      stat(
        percent(meta.headline.exBlackSummer),
        'excluding 2019–20',
        'without the Black Summer fire years',
        tip('A robustness check: if this differed sharply, the finding would be a bushfire artefact rather than a general property of Australian air.')
      )
    )
  );

  const spread = Object.entries(meta.headline.byJurisdiction)
    .filter(([, value]) => value)
    .map(([jurisdiction, value]) => `${jurisdiction} ${percent(value!.median)}`)
    .join(', ');
  headline.body.append(
    el(
      'p',
      'note',
      `Per jurisdiction: ${spread}. The figure is a median of station-years, never a mean of shares — one extraordinary year at one station should not be able to move a national statistic. ` +
        `Exposure is summed over hours where the instrument reported a value; ${glossaryLink(
          'capture',
          'data capture'
        )} below 75% disqualifies a station-year outright.`
    )
  );
  container.append(headline.root);

  // ── The grid ────────────────────────────────────────────────────────────────
  const grid = panel({
    title: 'Every station-year, worst hours first',
    subtitle:
      'Each square plots the cumulative share of that year’s exposure against the cumulative share of its hours, ranked worst first. The dashed diagonal is what the year would look like if every hour were the annual mean. The further the curve bows above it, the less the average describes.',
  });

  const jurisdictions = ['All', 'NSW', 'QLD', 'ACT'];
  let filter = 'All';
  let sort: 'share' | 'year' | 'peak' = 'share';

  const controls = el('div', 'control-row');
  const filterRow = el('div', 'segmented');
  for (const item of jurisdictions) {
    const button = el('button', `segment${item === filter ? ' segment-on' : ''}`);
    button.type = 'button';
    button.textContent = item;
    button.addEventListener('click', () => {
      filter = item;
      [...filterRow.children].forEach((child, index) =>
        child.classList.toggle('segment-on', jurisdictions[index] === filter)
      );
      draw();
    });
    filterRow.append(button);
  }
  controls.append(filterRow);

  const sortField = el('label', 'select-field');
  sortField.append(el('span', 'field-label', 'Sort by'));
  const sortSelect = el('select');
  sortSelect.setAttribute('aria-label', 'Sort station-years');
  for (const [value, label] of [
    ['share', 'Most burst-dominated'],
    ['peak', 'Worst single hour'],
    ['year', 'Most recent year'],
  ] as const) {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = label;
    sortSelect.append(option);
  }
  sortSelect.addEventListener('change', () => {
    sort = sortSelect.value as typeof sort;
    draw();
  });
  sortField.append(sortSelect);
  controls.append(sortField);
  grid.body.append(controls);

  grid.body.append(
    legend(
      ['NSW', 'QLD', 'ACT'].map((jurisdiction) => ({
        label: jurisdiction,
        hex: JURISDICTION_COLOUR[jurisdiction],
        tip: `${jurisdiction} station-years`,
      }))
    )
  );

  const gridInner = el('div', 'lorenz-grid');
  grid.body.append(gridInner);
  const more = el('button', 'button');
  more.type = 'button';

  let limit = 24;
  function draw(): void {
    const rows = lorenz
      .filter((row) => filter === 'All' || row.jurisdiction === filter)
      .sort((a, b) => {
        if (sort === 'year') return b.year - a.year || b.top1Share - a.top1Share;
        if (sort === 'peak') return b.worstHourValue - a.worstHourValue;
        return b.top1Share - a.top1Share;
      });
    gridInner.innerHTML = '';
    for (const row of rows.slice(0, limit)) {
      gridInner.append(lorenzCell(row, row.clusterId === state.station));
    }
    // A view that silently caps what it shows reads as "this is everything". Say what is hidden.
    more.textContent =
      limit >= rows.length
        ? `Showing all ${number(rows.length)} station-years`
        : `Show more — ${number(rows.length - limit)} of ${number(rows.length)} station-years not shown`;
    more.disabled = limit >= rows.length;
  }

  more.addEventListener('click', () => {
    limit += 48;
    draw();
  });
  draw();
  grid.body.append(more);

  const worst = lorenz[0];
  if (worst) {
    grid.body.append(
      el(
        'p',
        'note',
        `The most burst-dominated station-year in the archive is <strong>${worst.name} in ${worst.year}</strong>, where ${percent(
          worst.top1Share
        )} of the year's particle exposure arrived in ${worst.top1Hours} hours — with a single hour reaching ${concentration(
          worst.worstHourValue
        )} ${UNIT} at ${prettyHour(worst.worstHourAt)}. Its annual mean was ${concentration(
          worst.annualMean
        )}, which is a number no hour of that year resembled.`
      )
    );
  }
  container.append(grid.root);

  if (state.station) {
    const jump = el('div', 'control-row');
    const button = el('button', 'button');
    button.type = 'button';
    button.textContent = 'See the episodes behind these hours';
    button.addEventListener('click', () => setState({ view: 'ledger' }));
    jump.append(button);
    container.append(jump);
  }
}
